class TrInfinitePagination extends HTMLElement {
	selectors = {
		section: '[data-tr-js-infinite-pagination-section]',
		productList: '[data-tr-js-infinite-pagination-product-list]',
		nextLink: '[data-tr-js-infinite-pagination-next]',
		products: '[data-tr-js-infinite-pagination-products]',
		productContainer: '[data-tr-js-infinite-pagination-product-container]',
		articleContainer: '[data-tr-js-infinite-pagination-article-container]',
		articleList: '[data-tr-js-infinite-pagination-article-list]',
		pagination: '[data-tr-js-infinite-pagination]'
	};

	attributes = {
		sectionId: 'data-tr-js-infinite-pagination-section-id'
	};

	stateClasses = {
		isActive: 'is-active',
		isLoading: 'is-loading'
	};

	observer = null;

	isLoading = false;

	connectedCallback() {
		this.sectionElement = this.closest(this.selectors.section);
		this.productsElement = this.closest(this.selectors.products);
		this.productListElement = this.productsElement?.querySelector(
			this.selectors.productList
		);
		this.articleListElement = this.productsElement?.querySelector(this.selectors.articleList);
		this.nextLinkElement = this.querySelector(this.selectors.nextLink);

		this.init();
		this.bindEvents();
	}

	disconnectedCallback() {
		document.removeEventListener('shopify:section:load', this.onSectionLoad);

		this.destroy();
	}

	init() {
		this.destroy();

		if (!this.productsElement || !this.nextLinkElement) return;

		this.classList.add(this.stateClasses.isActive);
		this.observer = new IntersectionObserver(this.onIntersection, {
			rootMargin: '0px 0px 600px'
		});
		this.observer.observe(this);
	}

	bindEvents() {
		document.addEventListener('shopify:section:load', this.onSectionLoad);
	}

	onSectionLoad = event => {
		if (
			event.detail.sectionId !==
			this.sectionElement?.getAttribute(this.attributes.sectionId)
		)
			return;

		this.productsElement = this.closest(this.selectors.products);
		this.productListElement = this.productsElement?.querySelector(
			this.selectors.productList
		);
		this.articleListElement = this.productsElement?.querySelector(this.selectors.articleList);
		this.nextLinkElement = this.querySelector(this.selectors.nextLink);
		this.init();
	};

	onIntersection = entries => {
		if (!entries.some(entry => entry.isIntersecting)) return;

		this.loadNextPage();
	};

	async loadNextPage() {
		if (this.isLoading || !this.nextLinkElement || !this.sectionElement) return;

		this.isLoading = true;
		this.classList.add(this.stateClasses.isLoading);

		try {
			const url = new URL(this.nextLinkElement.href);
			const sectionId = this.sectionElement.getAttribute(this.attributes.sectionId);

			url.searchParams.set('section_id', sectionId);

			const response = await fetch(url);

			if (!response.ok) return;

			const documentElement = new DOMParser().parseFromString(
				await response.text(),
				'text/html'
			);
			const newProductsElement = documentElement.querySelector(
				this.selectors.products
			);
			const newPaginationElement = documentElement.querySelector(
				this.selectors.pagination
			);

			if (!newProductsElement || !newPaginationElement) return;

			const newProductListElement = newProductsElement.querySelector(
				this.selectors.productList
			);

			if (newProductListElement) {
				if (this.productListElement) {
					this.productListElement.append(
						...Array.from(newProductListElement.children, productElement =>
							document.importNode(productElement, true)
						)
					);
				} else {
					const newProductContainerElement = newProductsElement.querySelector(
						this.selectors.productContainer
					);
					const productContainerElement = document.importNode(newProductContainerElement, true);
					productContainerElement.querySelector(this.selectors.pagination)?.remove();
					this.productsElement.insertBefore(
						productContainerElement,
						this.productsElement.querySelector(this.selectors.articleContainer)
					);
					this.productListElement = this.productsElement.querySelector(this.selectors.productList);
				}
			}

			const newArticleListElement = newProductsElement.querySelector(this.selectors.articleList);
			if (newArticleListElement) {
				if (this.articleListElement) {
					this.articleListElement.append(
						...Array.from(newArticleListElement.children, articleElement =>
							document.importNode(articleElement, true)
						)
					);
				} else {
					const newArticleContainerElement = newProductsElement.querySelector(
						this.selectors.articleContainer
					);
					const articleContainerElement = document.importNode(newArticleContainerElement, true);
					articleContainerElement.querySelector(this.selectors.pagination)?.remove();
					this.productsElement.append(articleContainerElement);
					this.articleListElement = this.productsElement.querySelector(this.selectors.articleList);
				}
			}
			this.replaceChildren(
				...Array.from(newPaginationElement.childNodes, childNode =>
					document.importNode(childNode, true)
				)
			);
			this.nextLinkElement = this.querySelector(this.selectors.nextLink);

			if (!this.nextLinkElement) {
				this.observer?.disconnect();
			}
		} catch (error) {
			this.classList.remove(this.stateClasses.isActive);
		} finally {
			this.isLoading = false;
			this.classList.remove(this.stateClasses.isLoading);
		}
	}

	destroy() {
		this.observer?.disconnect();
		this.observer = null;
		this.isLoading = false;
		this.classList.remove(this.stateClasses.isActive);
		this.classList.remove(this.stateClasses.isLoading);
	}
}

if (!customElements.get('tr-infinite-pagination')) {
	customElements.define('tr-infinite-pagination', TrInfinitePagination);
}
